/* eslint-disable sort-keys */
import {useTranslation} from 'react-i18next';

import {ROUTES, URLS} from 'constants/routes';

import amazonMarquee from 'assets/v3/marquee/amazon.svg';
import aliexpressMarquee from 'assets/v3/marquee/aliexpress.svg';
import alibabaMarquee from 'assets/v3/marquee/alibaba.png';
import dianxiaomiMarquee from 'assets/v3/marquee/dianxiaomi.png';
import dropshippingMarquee from 'assets/v3/marquee/dropshipping.png';
import keyouyunMarquee from 'assets/v3/marquee/keyouyun.png';
import mangoerpMarquee from 'assets/v3/marquee/mangoerp.png';
import miaoshouerpMarquee from 'assets/v3/marquee/miaoshouerp.png';
import wangxiaowangMarquee from 'assets/v3/marquee/wangxiaowang.png';
import fusionProductImg from 'assets/v3/products/fusion.webp';
import imageTranslatorProductImg from 'assets/v3/products/image-translator.webp';
import listingProductImg from 'assets/v3/products/listing.webp';
import videoTranslatorHeroImg from 'assets/v3/video-translator/hero.webp';
import ebayMarquee from 'assets/v3/marquee/ebay.svg';
import lazadaMarquee from 'assets/v3/marquee/lazada.png';
import shopeeMarquee from 'assets/v3/marquee/shopee.svg';
import shopifyMarquee from 'assets/v3/marquee/shopify.svg';
import temuMarquee from 'assets/v3/marquee/temu.png';
import tiktokMarquee from 'assets/v3/marquee/tiktok-shop.svg';
import amazonOrbit from 'assets/v3/orbit/amazon.svg';
import aliexpressOrbit from 'assets/v3/orbit/aliexpress.svg';
import ebayOrbit from 'assets/v3/orbit/ebay.svg';
import instagramOrbit from 'assets/v3/orbit/instagram.svg';
import lazadaOrbit from 'assets/v3/orbit/lazada.svg';
import metaAdsOrbit from 'assets/v3/orbit/meta-ads.svg';
import shopifyOrbit from 'assets/v3/orbit/shopify.svg';
import temuOrbit from 'assets/v3/orbit/temu.svg';
import tiktokOrbit from 'assets/v3/orbit/tiktok-shop.svg';
import xOrbit from 'assets/v3/orbit/x.svg';
import youtubeOrbit from 'assets/v3/orbit/youtube.svg';

/**
 * 新版首页数据，文案/坐标/时序全部照抄设计稿 docs/designs/xiangji_home_v3/index.html。
 * 设计稿中的既有差异（如「50+语言」标签缺 glass-card、产品卡1 无链接）按原样保留，勿"修复"。
 */

/* ---------- 轨道相位契约 ---------- */
/** 外环（平台）52s 顺时针：animation-delay = -52s × a/360 */
export const outerOrbitDelay = (angleDeg: number) => `${((-52 * angleDeg) / 360).toFixed(2)}s`;

/* ---------- Hero 粒子 ---------- */
export interface ParticleSpec {
  top: string;
  left: string;
  animationDelay: string;
}

export const particles: ParticleSpec[] = [
  {animationDelay: '0s', left: '10%', top: '20%'},
  {animationDelay: '1s', left: '25%', top: '40%'},
  {animationDelay: '2s', left: '50%', top: '15%'},
  {animationDelay: '0.5s', left: '70%', top: '60%'},
  {animationDelay: '1.5s', left: '85%', top: '30%'},
  {animationDelay: '3s', left: '40%', top: '75%'},
  {animationDelay: '2.5s', left: '60%', top: '50%'},
  {animationDelay: '1.2s', left: '15%', top: '80%'},
  {animationDelay: '0.8s', left: '75%', top: '10%'},
  {animationDelay: '2.2s', left: '90%', top: '65%'},
];

/* ---------- 轨道外环（跨境电商/广告平台，12 个，每 30°） ---------- */
export interface OuterOrbitLabel {
  title: string;
  angleDeg: number;
  /** 字标/图标资源的引用方式：ES import 的资源路径 */
  icon?: string;
  /** 图标尺寸类（'icon' 为设计稿残留的无定义类，原样保留） */
  iconClass?: string;
  /** 是否显示名称文字 */
  showName?: string;
  /** 330°「更多」项：无 logo，渲染渐变圆点 */
  dotChip?: boolean;
}

export const outerOrbitLabels: OuterOrbitLabel[] = [
  {angleDeg: 0, icon: amazonOrbit, iconClass: 'icon', showName: 'Amazon', title: 'Amazon'},
  {angleDeg: 30, icon: ebayOrbit, iconClass: 'icon', title: 'eBay'},
  {angleDeg: 60, icon: aliexpressOrbit, iconClass: 'icon', title: 'AliExpress'},
  {angleDeg: 90, icon: temuOrbit, iconClass: 'h-6 w-6', showName: 'Temu', title: 'Temu'},
  {angleDeg: 120, icon: tiktokOrbit, iconClass: 'h-5 w-auto', showName: 'TikTok Shop', title: 'TikTok Shop'},
  {angleDeg: 150, icon: lazadaOrbit, iconClass: 'icon', title: 'Lazada'},
  {angleDeg: 180, icon: shopifyOrbit, iconClass: 'icon', title: 'Shopify'},
  {angleDeg: 210, icon: metaAdsOrbit, iconClass: 'h-5 w-auto', showName: 'Meta Ads', title: 'Meta Ads'},
  {angleDeg: 240, icon: instagramOrbit, iconClass: 'h-5 w-auto', showName: 'Instagram', title: 'Instagram'},
  {angleDeg: 270, icon: xOrbit, iconClass: 'h-5 w-auto', title: 'X'},
  {angleDeg: 300, icon: youtubeOrbit, iconClass: 'h-5 w-auto', showName: 'Youtube', title: 'Youtube'},
  {angleDeg: 330, dotChip: true, title: '更多平台'},
];

/* ---------- 数据统计 ---------- */
export interface StatSpec {
  /** 稳定 key（非文案），供列表渲染用——label 随语言切换变化，不能拿来当 React key */
  key: string;
  target: number;
  suffix: string;
  label: string;
  transitionDelay?: string;
}

interface StatValue {
  target: number;
  suffix: string;
}

/**
 * 各语言的数字+单位写法（按语言数量级习惯直接给「准确数值」，不做换算）：
 * 中文/韩文有「万/亿」进位习惯，target 用小数字、suffix 带大单位（10万+/100万张/日/10亿张）；
 * 英文/俄文没有该习惯，直接给出对应量级的 target + 单位后缀（100K+/1M+/day/1B+）。
 * 四项数值口径：卖家 10万 = 100,000；处理量 100万张/日 = 1,000,000/日；
 * 地区 8 个市场；累计生成 10亿张 = 1,000,000,000 张——四语言必须换算成同一实际数值，
 * 不能只套用别的语言的 target 数字（如英文曾错误沿用中文 target=10 配 'B+'，
 * 变成 10,000,000,000，超出真实值 10 倍）。
 */
const STATS_BY_LANGUAGE: Record<
  'en' | 'ko' | 'ru' | 'zh-CN',
  Record<'images' | 'regions' | 'sellers' | 'totalImages', StatValue>
> = {
  en: {
    images: {suffix: 'M+/day', target: 1},
    regions: {suffix: '+ Markets', target: 8},
    sellers: {suffix: '0K+', target: 10},
    totalImages: {suffix: 'B+', target: 1},
  },
  ko: {
    images: {suffix: '만장/일', target: 100},
    regions: {suffix: '개 시장', target: 8},
    sellers: {suffix: '만+', target: 10},
    totalImages: {suffix: '억장', target: 10},
  },
  ru: {
    images: {suffix: 'млн+/день', target: 1},
    regions: {suffix: '+ рынков', target: 8},
    sellers: {suffix: 'тыс.+', target: 100},
    totalImages: {suffix: 'млрд+', target: 1},
  },
  'zh-CN': {
    images: {suffix: '万张/日', target: 100},
    regions: {suffix: '大市场', target: 8},
    sellers: {suffix: '万+', target: 10},
    totalImages: {suffix: '亿张', target: 10},
  },
};

/** 数据统计工厂（i18n）：卖家规模、处理量、覆盖地区、累计生成量，四语言各自换算真实数值 */
export const useStats = (): StatSpec[] => {
  const {i18n, t} = useTranslation('home');
  const values = STATS_BY_LANGUAGE[i18n.language as keyof typeof STATS_BY_LANGUAGE] || STATS_BY_LANGUAGE['zh-CN'];

  return [
    {
      key: 'sellers',
      label: t('stats.sellers'),
      suffix: values.sellers.suffix,
      target: values.sellers.target,
      transitionDelay: undefined,
    },
    {
      key: 'images',
      label: t('stats.images'),
      suffix: values.images.suffix,
      target: values.images.target,
      transitionDelay: '0.15s',
    },
    {
      key: 'regions',
      label: t('stats.regions'),
      suffix: values.regions.suffix,
      target: values.regions.target,
      transitionDelay: '0.3s',
    },
    {
      key: 'totalImages',
      label: t('stats.totalImages'),
      suffix: values.totalImages.suffix,
      target: values.totalImages.target,
      transitionDelay: '0.45s',
    },
  ];
};

/* ---------- 五大 AI 服务（产品卡） ---------- */
export interface ProductCardSpec {
  /** 稳定 key（非文案），供列表渲染用——title 随语言切换变化，不能拿来当 React key */
  key: string;
  title: string;
  description: string;
  tags: string[];
  imageSrc: string;
  imageAlt: string;
  iconPaths: string[];
  iconBgClass: string;
  iconColorClass: string;
  iconHoverBgClass: string;
  /** 站内路由（Link）；站外链接（a）；卡片1 设计稿即无链接 */
  to?: string;
  href?: string;
  transitionDelay: string;
}

/** 五大 AI 服务（产品卡）工厂（i18n）：文案/tags/alt 走 home 词典，图片与样式静态 */
export const useProductCards = (): ProductCardSpec[] => {
  const {t} = useTranslation('home');
  // tags 为字符串数组，走 returnObjects（唯一使用点，受控）
  const tags = (key: string) => t(key, {returnObjects: true}) as string[];

  return [
    {
      description: t('products.marketing.description'),
      iconBgClass: 'bg-rose-50',
      iconColorClass: 'text-rose-600',
      iconHoverBgClass: 'group-hover:bg-rose-100',
      iconPaths: ['M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z', 'M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z'],
      imageAlt: t('products.marketing.imageAlt'),
      imageSrc: 'https://q6.itc.cn/q_70/images03/20260214/8ab5513dff534ea08761d3f16d323552.png',
      key: 'marketing',
      tags: tags('products.marketing.tags'),
      title: t('products.marketing.title'),
      to: ROUTES.marketing,
      transitionDelay: '0s',
    },
    {
      description: t('products.detailPage.description'),
      href: URLS.detailPage,
      iconBgClass: 'bg-emerald-50',
      iconColorClass: 'text-emerald-600',
      iconHoverBgClass: 'group-hover:bg-emerald-100',
      iconPaths: [
        'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
      ],
      imageAlt: t('products.detailPage.imageAlt'),
      imageSrc: listingProductImg,
      key: 'detailPage',
      tags: tags('products.detailPage.tags'),
      title: t('products.detailPage.title'),
      transitionDelay: '0.1s',
    },
    {
      description: t('products.imageTranslator.description'),
      iconBgClass: 'bg-indigo-50',
      iconColorClass: 'text-indigo-600',
      iconHoverBgClass: 'group-hover:bg-indigo-100',
      iconPaths: [
        'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
      ],
      imageAlt: t('products.imageTranslator.imageAlt'),
      imageSrc: imageTranslatorProductImg,
      key: 'imageTranslator',
      tags: tags('products.imageTranslator.tags'),
      title: t('products.imageTranslator.title'),
      to: ROUTES.imageTranslator,
      transitionDelay: '0.2s',
    },
    {
      description: t('products.videoTranslator.description'),
      iconBgClass: 'bg-violet-50',
      iconColorClass: 'text-violet-600',
      iconHoverBgClass: 'group-hover:bg-violet-100',
      iconPaths: [
        'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z',
      ],
      imageAlt: t('products.videoTranslator.imageAlt'),
      imageSrc: videoTranslatorHeroImg,
      key: 'videoTranslator',
      tags: tags('products.videoTranslator.tags'),
      title: t('products.videoTranslator.title'),
      to: ROUTES.videoTranslator,
      transitionDelay: '0.3s',
    },
    {
      description: t('products.fusion.description'),
      iconBgClass: 'bg-amber-50',
      iconColorClass: 'text-amber-600',
      iconHoverBgClass: 'group-hover:bg-amber-100',
      iconPaths: [
        'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
      ],
      imageAlt: t('products.fusion.imageAlt'),
      imageSrc: fusionProductImg,
      key: 'fusion',
      tags: tags('products.fusion.tags'),
      title: t('products.fusion.title'),
      to: ROUTES.fusion,
      transitionDelay: '0.4s',
    },
  ];
};

/* ---------- 跑马灯（8 logo × 2 组，无缝滚动依赖两组等宽） ---------- */
export interface MarqueeItem {
  name: string;
  imgSrc: string;
  /** lazada/temu 为彩色 PNG，加 grayscale 灰化 */
  grayscale?: boolean;
  /** 横向组合标（图内自带文字）：只渲染图片（默认 h-6 宽自适应），不显示 name 文字 */
  wide?: boolean;
  /** wide 模式的尺寸类覆盖（如个别 logo 需要更大/更小：'h-7 w-auto'） */
  imgClass?: string;
}

export const marqueeItems: MarqueeItem[] = [
  // ERP/服务商伙伴（2026-09-16 业务新增，彩色 PNG 统一 grayscale 灰化，置于平台 logo 之前）
  {grayscale: true, imgSrc: alibabaMarquee, name: 'Alibaba', wide: true},
  {grayscale: true, imgSrc: dianxiaomiMarquee, name: '店小秘', wide: true},
  {grayscale: true, imgSrc: dropshippingMarquee, name: 'CJ Dropshipping', wide: true},
  {grayscale: true, imgSrc: keyouyunMarquee, name: '客优云', wide: true},
  {grayscale: true, imgSrc: mangoerpMarquee, name: '芒果店长', wide: true}, // 原图上下白边已裁（800→392 内容高），无需再放大
  {grayscale: true, imgSrc: miaoshouerpMarquee, name: '妙手ERP', wide: true},
  {grayscale: true, imgClass: 'h-5 w-auto', imgSrc: wangxiaowangMarquee, name: '旺销王', wide: true},
  {imgSrc: amazonMarquee, name: 'Amazon'},
  {imgSrc: ebayMarquee, name: 'eBay'},
  {imgSrc: shopifyMarquee, name: 'Shopify'},
  {imgSrc: aliexpressMarquee, name: 'AliExpress'},
  {imgSrc: shopeeMarquee, name: 'Shopee'},
  {grayscale: true, imgSrc: lazadaMarquee, name: 'Lazada'},
  {grayscale: true, imgSrc: temuMarquee, name: 'Temu'},
  {imgSrc: tiktokMarquee, name: 'TikTok Shop'},
];

/* ---------- 用户评价 ---------- */
export interface TestimonialSpec {
  /** 稳定 key（非文案），供列表渲染用——name 随语言切换变化（人名音译），不能拿来当 React key */
  key: string;
  quote: string;
  avatarSrc: string;
  name: string;
  role: string;
  transitionDelay?: string;
}

/** 用户评价工厂（i18n）：quote/name/role 走 home 词典 */
export const useTestimonials = (): TestimonialSpec[] => {
  const {t} = useTranslation('home');

  return [
    {
      avatarSrc: 'https://ai-public.mastergo.com/ai/img_res/1786884170746WXYZAbcdef12345GHIJ.jpg',
      key: 'zhangming',
      name: t('trust.zhangming.name'),
      quote: t('trust.zhangming.quote'),
      role: t('trust.zhangming.role'),
      transitionDelay: undefined,
    },
    {
      avatarSrc: 'https://ai-public.mastergo.com/ai/img_res/1786884170746KLMNOpqrst12345UVWX.jpg',
      key: 'liting',
      name: t('trust.liting.name'),
      quote: t('trust.liting.quote'),
      role: t('trust.liting.role'),
      transitionDelay: '0.15s',
    },
    {
      avatarSrc: 'https://ai-public.mastergo.com/ai/img_res/1786884170746YZABCdefgh12345IJKL.jpg',
      key: 'wanglei',
      name: t('trust.wanglei.name'),
      quote: t('trust.wanglei.quote'),
      role: t('trust.wanglei.role'),
      transitionDelay: '0.3s',
    },
  ];
};

/* ---------- Hero 信任徽章头像 ---------- */
export const trustAvatars = [
  'https://ai-public.mastergo.com/ai/img_res/1786884170746ABCDEfghij12345KLMN.jpg',
  'https://ai-public.mastergo.com/ai/img_res/1786884170746FGHIJklmno67890OPQR.jpg',
  'https://ai-public.mastergo.com/ai/img_res/1786884170746STUVWxyzab12345CDEF.jpg',
];

/* ---------- 图标 path 复用 ---------- */
export const iconPaths = {
  arrowRight: 'M13 7l5 5m0 0l-5 5m5-5H6',
  chevronRight: 'M9 5l7 7-7 7',
  play: [
    'M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z',
    'M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  ],
  star: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z',
};
